import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, ChevronRight, FileText, GripVertical, MapPin, RefreshCcw, Route, Trash2, Truck } from "lucide-react";
import "../../../style/style-cargando.css";
import cargandoCard from "../../../components/cargando-card";
import sinAccesoCard from "../../../components/sin-acceso-card";
import ProcessingDialog from "../../../components/processing-dialog";
import ToastComponent from "../../../components/toast-component";
import ModalConfirmar from "../../../components/modal-confirmar";
import { obtenerListaKanban } from "../../../api/api-kanban";
import { obtenerStatusKanban } from "../../../api/api-status-kanban";
import { obtenerListaProgramacionCamionesAprobados } from "../../../api/api-programacion-camiones";
import { obtenerListaSolicitudProductoAprobado } from "../../../api/api-solicitud-producto";
import { obtenerPuntoActualVehiculo } from "../../../api/gestion-dts/gestion";
import { TIPO_INPUT } from "../tipo_input";
import { obtenerPermisoRolUsuarioWeb } from "../../../api/api-permiso-rol-usuario-web";
import {
    actualizarEstadoViajeOrdenKanban,
    actualizarViajeOrdenKanban,
    crearViajeOrdenKanban,
    deshabilitarViajeOrdenKanban,
    obtenerVigentePorPlaca,
} from "../../../api/api-viaje-orden-kanban";
import {
    STRING_ID_TIPO_USUARIO_WEB,
    STRING_SUPER_ADMIN,
    STRING_ID_ROL_USUARIO_WEB,
    STRING_ID_EMPRESA_ULTIMA_MILLA_V3,
    STRING_ID_USUARIO_WEB,
} from "../../../utils/const/string-const";
import { PAGE_ID } from "../../usuarios/gestionar-rol-usuario-web/pages-const";
import { sharedHaversine } from "../../../utils/shared-functions";
import MapaViajeGoogle from "./mapa-viaje-google";

const ID_TABLERO = "viaje";
const ID_TABLERO_PRODUCTO = "producto";

const PRODUCTO_META_TO_VIAJE_KEY = {
    [TIPO_INPUT.GEOCERCA_ORIGEN]: "geocercaorigen",
    [TIPO_INPUT.GEOCERCA_DESTINO]: "geocercadestino",
    [TIPO_INPUT.GEORUTA]: "seleccionegeoruta",
    [TIPO_INPUT.FECHA_SALIDA]: "fechainicioviaje",
    [TIPO_INPUT.FECHA_LLEGADA]: "fechafinviaje",
    [TIPO_INPUT.HORA_SALIDA]: "ingresehorapartida",
    [TIPO_INPUT.HORA_LLEGADA]: "horallegada",
    [TIPO_INPUT.SELECCIONE_VEHICULO]: "seleccionevehiculo",
};

const parseMetaArray = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string" && raw.trim().length > 0) {
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch (_) {
            return [];
        }
    }
    return [];
};

const transformProductoAsViaje = (productoItem) => {
    const metaArr = parseMetaArray(productoItem?.meta);
    const respuestasParsed = metaArr
        .filter((m) => PRODUCTO_META_TO_VIAJE_KEY[m?.type])
        .map((m, idx) => ({
            id: m?.id ?? `producto-${productoItem?.id}-${idx}`,
            key: PRODUCTO_META_TO_VIAJE_KEY[m.type],
            type: m.type,
            label: m?.label ?? "",
            value: m?.value,
        }));
    return {
        ...productoItem,
        formularioViaje:
            productoItem?.descripcion ??
            productoItem?.formularioViaje ??
            null,
        respuestasParsed,
        __source: "producto",
    };
};

const parseProgramacionCamionesItem = (row) => {
    let respuestasParsed = [];
    try {
        if (typeof row?.respuestas === "string" && row.respuestas.trim().length > 0) {
            respuestasParsed = JSON.parse(row.respuestas);
        } else if (Array.isArray(row?.respuestas)) {
            respuestasParsed = row.respuestas;
        }
    } catch (err) {
        console.log("parseProgramacionCamionesItem err");
        console.log(err);
    }
    return { ...row, respuestasParsed };
};

const parseStatusChildren = (children) => {
    if (Array.isArray(children)) return children;
    if (typeof children === "string" && children.trim()) {
        try {
            const parsed = JSON.parse(children);
            return Array.isArray(parsed) ? parsed : [];
        } catch (_) {
            return [];
        }
    }
    return [];
};

const esColumnaIniciaViaje = (column) => {
    const flag = column?.esteEstadoIniciaViaje;
    return flag === true || flag === 1 || flag === "1";
};

const getRespuestaByKey = (viaje, key) => {
    const arr = viaje?.respuestasParsed;
    if (!Array.isArray(arr)) return null;
    return arr.find((r) => r?.key === key) ?? null;
};

// Busca una respuesta primero por `key` (legacy) y, si no encuentra o el value está vacío,
// cae al flag semántico (nuevo esquema: geocerca_origen, vehiculo_viaje, etc.).
const getRespuestaByKeyOrFlag = (viaje, key, flag) => {
    const arr = viaje?.respuestasParsed;
    if (!Array.isArray(arr)) return null;
    const byKey = arr.find((r) => r?.key === key);
    if (byKey && byKey.value != null && byKey.value !== "") return byKey;
    const byFlag = arr.find((r) => r?.[flag] === true);
    return byFlag ?? byKey ?? null;
};

// Types que representan lo mismo entre camiones y productos.
// Camiones (formulario-viaje): "seleccione_origen" / "seleccione_destino" / "seleccione_georuta".
// Productos (después de transformProductoAsViaje): "geocerca_origen" / "geocerca_destino" / "georuta".
const ORIGEN_TYPES = new Set(["seleccione_origen", TIPO_INPUT.GEOCERCA_ORIGEN]);
const DESTINO_TYPES = new Set(["seleccione_destino", TIPO_INPUT.GEOCERCA_DESTINO]);
const GEORUTA_TYPES = new Set(["seleccione_georuta", TIPO_INPUT.GEORUTA]);

const getRespuestaByTypes = (viaje, typeSet) => {
    const arr = viaje?.respuestasParsed;
    if (!Array.isArray(arr)) return null;
    return arr.find((r) => typeSet.has(r?.type)) ?? null;
};

const coordCentroid = (coordenadas) => {
    if (!Array.isArray(coordenadas) || coordenadas.length === 0) return null;
    const raw = Array.isArray(coordenadas[0]) ? coordenadas : [coordenadas];
    const ring = raw[0];
    if (!Array.isArray(ring) || ring.length === 0) return null;
    let sLat = 0;
    let sLng = 0;
    let n = 0;
    for (const c of ring) {
        if (c == null || typeof c !== "object") continue;
        const lat = parseFloat(c.x ?? c.lat ?? c.latitud);
        const lng = parseFloat(c.y ?? c.lng ?? c.longitud);
        if (!isFinite(lat) || !isFinite(lng)) continue;
        sLat += lat;
        sLng += lng;
        n += 1;
    }
    if (n === 0) return null;
    return { lat: sLat / n, lng: sLng / n };
};

const resolveGeocercaValue = (viaje, tipo) => {
    // tipo: "origen" | "destino" | "georuta"
    const typeSet =
        tipo === "origen"
            ? ORIGEN_TYPES
            : tipo === "destino"
                ? DESTINO_TYPES
                : GEORUTA_TYPES;
    // 1) por type (fuente de verdad estable entre camiones y productos)
    let value = getRespuestaByTypes(viaje, typeSet)?.value;
    if (value && typeof value === "object") return value;
    // 2) fallback por key (para viejos formularios que solo tienen la key transformada)
    const legacyKey =
        tipo === "origen"
            ? "geocercaorigen"
            : tipo === "destino"
                ? "geocercadestino"
                : "seleccionegeoruta";
    value = getRespuestaByKey(viaje, legacyKey)?.value;
    if (value && typeof value === "object") return value;
    return null;
};

const getGeocercaCentroid = (viaje, tipo) => {
    const value = resolveGeocercaValue(viaje, tipo);
    if (!value) return null;
    return coordCentroid(value.coordenadas);
};

const getGeocercaLabel = (viaje, tipo) => {
    const value = resolveGeocercaValue(viaje, tipo);
    return value?.label ?? null;
};

/**
 * Greedy nearest-neighbor con restricción de pickup/delivery.
 * Cada solicitud aporta 2 nodos: origen (recogida) y destino (entrega).
 * No se puede visitar el destino sin haber visitado antes el origen de la misma solicitud.
 * @param {{lat:number,lng:number}|null} startPos
 * @param {Array<{id, formularioViaje?, origen, destino, origenLabel?, destinoLabel?}>} pedidos
 * @returns {{secuencia: Array, totalKm: number}}
 */
const formatDuracion = (min) => {
    if (!isFinite(min) || min <= 0) return "0 min";
    const rounded = Math.round(min);
    if (rounded < 60) return `${rounded} min`;
    const h = Math.floor(rounded / 60);
    const m = rounded % 60;
    return m === 0 ? `${h} h` : `${h} h ${m} min`;
};

const computarOrdenRecomendado = (startPos, pedidos) => {
    const estado = (Array.isArray(pedidos) ? pedidos : [])
        .filter((p) => p?.origen && p?.destino)
        .map((p) => ({ ...p, pickedUp: false, delivered: false }));
    const secuencia = [];
    let actual = startPos;
    let totalKm = 0;

    while (estado.some((p) => !p.delivered)) {
        const candidatos = [];
        for (const p of estado) {
            if (!p.pickedUp) candidatos.push({ tipo: "origen", pedido: p, coord: p.origen, label: p.origenLabel });
            else if (!p.delivered) candidatos.push({ tipo: "destino", pedido: p, coord: p.destino, label: p.destinoLabel });
        }
        if (candidatos.length === 0) break;

        let mejor = null;
        let mejorMetros = Infinity;
        for (const c of candidatos) {
            const metros = actual
                ? sharedHaversine(actual.lat, actual.lng, c.coord.lat, c.coord.lng)
                : 0;
            if (metros == null) continue;
            if (metros < mejorMetros) {
                mejorMetros = metros;
                mejor = c;
            }
        }
        if (!mejor) break;

        // sharedHaversine devuelve METROS -> convertimos a km para el UI.
        // duracionMin queda null hasta que Directions responda con el valor real.
        const distKm = mejorMetros / 1000;
        secuencia.push({
            tipo: mejor.tipo,
            pedidoId: mejor.pedido.id,
            pedidoLabel: mejor.pedido.formularioViaje ?? mejor.pedido.id,
            geocercaLabel: mejor.label,
            coord: mejor.coord,
            distanciaKm: distKm,
            duracionMin: null,
        });
        totalKm += distKm;
        actual = mejor.coord;
        if (mejor.tipo === "origen") mejor.pedido.pickedUp = true;
        else mejor.pedido.delivered = true;
    }

    return { secuencia, totalKm, totalMinutos: null };
};

/**
 * Consulta Google Directions y devuelve legs con distancia real (km) y duración (min).
 * Segmenta cuando hay >25 stops. Devuelve null si falla.
 * @param {Array<{lat:number,lng:number}>} pathPoints
 * @returns {Promise<Array<{distanciaKm:number, duracionMin:number}>|null>}
 */
const fetchDirectionsLegs = (pathPoints) => {
    return new Promise((resolve) => {
        const maps = window.google?.maps;
        if (!maps || !pathPoints || pathPoints.length < 2) return resolve(null);
        const MAX_WAYPOINTS = 23;
        const MAX_STOPS = 25;
        const svc = new maps.DirectionsService();
        const segments = [];
        let start = 0;
        while (start < pathPoints.length - 1) {
            const end = Math.min(start + MAX_STOPS - 1, pathPoints.length - 1);
            segments.push({
                origin: pathPoints[start],
                destination: pathPoints[end],
                waypoints: pathPoints
                    .slice(start + 1, end)
                    .slice(0, MAX_WAYPOINTS),
            });
            start = end;
        }
        const allLegs = [];
        const runSegment = (idx) => {
            if (idx >= segments.length) {
                resolve(allLegs);
                return;
            }
            const { origin, destination, waypoints } = segments[idx];
            svc.route(
                {
                    origin,
                    destination,
                    waypoints: waypoints.map((p) => ({ location: p, stopover: true })),
                    travelMode: maps.TravelMode.DRIVING,
                    unitSystem: maps.UnitSystem.METRIC,
                    optimizeWaypoints: false,
                    provideRouteAlternatives: false,
                },
                (result, status) => {
                    if (status !== maps.DirectionsStatus.OK || !result.routes?.[0]) {
                        return resolve(null);
                    }
                    (result.routes[0].legs || []).forEach((leg) => {
                        allLegs.push({
                            distanciaKm: (leg?.distance?.value ?? 0) / 1000,
                            duracionMin: (leg?.duration?.value ?? 0) / 60,
                        });
                    });
                    runSegment(idx + 1);
                }
            );
        };
        runSegment(0);
    });
};

const getPlacaFromViaje = (viaje) => {
    const vehiculo = getRespuestaByKeyOrFlag(viaje, "seleccionevehiculo", "vehiculo_viaje")?.value;
    if (vehiculo == null) return null;
    if (typeof vehiculo === "object") return vehiculo.value ?? vehiculo.label ?? null;
    return String(vehiculo);
};

const collectItemsForColumn = (columnId, programacionLista, statusLista) => {
    const status = Array.isArray(statusLista) ? statusLista : [];
    const columnStatus = status.find(
        (s) => String(s?.idKanban) === String(columnId)
    );
    if (!columnStatus) return [];
    const rawChildren = parseStatusChildren(columnStatus.children);
    if (!Array.isArray(programacionLista)) {
        return rawChildren.map(parseProgramacionCamionesItem);
    }
    const byId = new Map(
        programacionLista.filter((p) => p?.id != null).map((p) => [String(p.id), p])
    );
    return rawChildren
        .filter((item) => item?.id != null && byId.has(String(item.id)))
        .map((item) => byId.get(String(item.id)));
};

export default function MapaViajePage() {
    const idTipoUsuarioWeb = localStorage.getItem(STRING_ID_TIPO_USUARIO_WEB);
    const { t } = useTranslation();
    const [cargandoPage, setCargandoPage] = useState(true);
    const [tieneAccesoAlModulo, setTieneAccesoAlModulo] = useState(false);
    const [processingVisible, setProcessingVisible] = useState(false);
    const [toastVisible, setToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastType, setToastType] = useState("warning");

    const [viajes, setViajes] = useState([]);
    const [viajeSelected, setViajeSelected] = useState(null);
    const [puntoActual, setPuntoActual] = useState(null);
    const [sidebarVisible, setSidebarVisible] = useState(true);
    const [modoVisualizacion, setModoVisualizacion] = useState("todos"); // "todos" | "seleccionados"
    const [idsSeleccionados, setIdsSeleccionados] = useState(() => new Set());
    const [fuenteFilter, setFuenteFilter] = useState("todos"); // "todos" | "viaje" | "producto"
    const [ordenRecomendado, setOrdenRecomendado] = useState(null); // { placa, secuencia, totalKm, pedidosCount } | null
    const [ordenPanelVisible, setOrdenPanelVisible] = useState(false);
    const [dibujarRutaSugerida, setDibujarRutaSugerida] = useState(false);
    const [modalRecalcularVisible, setModalRecalcularVisible] = useState(false);
    const [modalDeshabilitarVisible, setModalDeshabilitarVisible] = useState(false);
    // Contador para invalidar callbacks async (Directions, hydrate) cuando cambia el viaje.
    const ordenMutRef = useRef(0);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [puntosVehiculos, setPuntosVehiculos] = useState({}); // { [placa]: punto }

    const getSource = (v) => v?.__source ?? "viaje";
    const viajesPorFuente =
        fuenteFilter === "todos"
            ? viajes
            : viajes.filter((v) => getSource(v) === fuenteFilter);
    const viajesVisibles =
        modoVisualizacion === "seleccionados"
            ? viajesPorFuente.filter((v) => idsSeleccionados.has(String(v?.id)))
            : viajesPorFuente;

    useEffect(() => {
        iniciar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Poll current vehicle position for the selected viaje every 15s
    useEffect(() => {
        setPuntoActual(null);
        if (viajeSelected == null) return;
        const placa = getPlacaFromViaje(viajeSelected);
        if (!placa) return;
        let cancelled = false;
        const fetchPunto = async () => {
            try {
                const punto = await obtenerPuntoActualVehiculo(String(placa).trim());
                if (!cancelled) setPuntoActual(punto);
            } catch (_) {
                if (!cancelled) setPuntoActual(null);
            }
        };
        fetchPunto();
        const intervalId = window.setInterval(fetchPunto, 15000);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [viajeSelected]);

    // Al cambiar el viaje seleccionado: reset del panel y auto-load del orden vigente (si hay).
    useEffect(() => {
        // Invalida callbacks async previos (Directions, hydrate) para evitar sobre-escrituras stale
        ordenMutRef.current += 1;
        // Reset local siempre que cambia la selección
        setOrdenRecomendado(null);
        setOrdenPanelVisible(false);
        setDibujarRutaSugerida(false);

        if (viajeSelected == null) return;
        const placa = getPlacaFromViaje(viajeSelected);
        if (!placa) return;
        const idEmpresa = localStorage.getItem(STRING_ID_EMPRESA_ULTIMA_MILLA_V3);
        if (!idEmpresa) return;

        let cancelled = false;
        (async () => {
            const vigente = await obtenerVigentePorPlaca({
                idEmpresa,
                placa: String(placa).trim(),
            });
            if (cancelled || vigente == null) return;

            // Hidratar el shape que consume el panel a partir del payload del backend
            const secuencia = Array.isArray(vigente.secuencia) ? vigente.secuencia : [];
            const startPos =
                secuencia.length > 0 && secuencia[0]?.coord
                    ? {
                        lat: parseFloat(secuencia[0].coord.lat),
                        lng: parseFloat(secuencia[0].coord.lng),
                    }
                    : null;

            setOrdenRecomendado({
                id: vigente.id,
                placa: vigente.placa,
                idEmpresa: vigente.idEmpresa,
                secuencia,
                totalKm: Number(vigente.totalKm ?? 0),
                totalMinutos:
                    vigente.totalMinutos != null ? Number(vigente.totalMinutos) : null,
                pedidosCount: Number(vigente.pedidosCount ?? secuencia.length),
                pedidosIds: Array.isArray(vigente.pedidosIds) ? vigente.pedidosIds : [],
                fechaInicioViaje: vigente.fechaInicioViaje ?? null,
                fechaFinViaje: vigente.fechaFinViaje ?? null,
                estado: vigente.estado ?? "borrador",
                nota: vigente.nota ?? null,
                modificadoManual: Number(vigente.modificadoManual ?? 0),
                origen: vigente.origen ?? null,
                startPos,
                desdeGps: false, // vino del backend, no del GPS actual
                directionsPending: false,
                directionsOk: null,
                dirty: false, // recién cargado, sin cambios locales
            });
            setOrdenPanelVisible(true);
        })();

        return () => {
            cancelled = true;
        };
    }, [viajeSelected]);

    // Poll current position for every vehicle that has at least one visible card
    const placasVisibles = Array.from(
        new Set(
            (Array.isArray(viajesVisibles) ? viajesVisibles : [])
                .map((v) => getPlacaFromViaje(v))
                .filter((p) => typeof p === "string" && p.trim().length > 0)
                .map((p) => String(p).trim())
        )
    );
    const placasKey = placasVisibles.slice().sort().join(",");
    useEffect(() => {
        if (placasVisibles.length === 0) {
            setPuntosVehiculos({});
            return;
        }
        let cancelled = false;
        const fetchAll = async () => {
            try {
                const entries = await Promise.all(
                    placasVisibles.map(async (placa) => {
                        try {
                            const punto = await obtenerPuntoActualVehiculo(placa);
                            return [placa, punto ?? null];
                        } catch (_) {
                            return [placa, null];
                        }
                    })
                );
                if (cancelled) return;
                const next = {};
                entries.forEach(([placa, punto]) => {
                    next[placa] = punto;
                });
                setPuntosVehiculos(next);
            } catch (_) {
                /* ignore */
            }
        };
        fetchAll();
        const intervalId = window.setInterval(fetchAll, 15000);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [placasKey]);

    const iniciar = async () => {
        let tieneAcceso = false;
        try {
            let objEnviar = {};
            objEnviar.idTablero = ID_TABLERO;
            if (idTipoUsuarioWeb === STRING_SUPER_ADMIN) {
                tieneAcceso = true;
            } else {
                const idRolUsuarioWeb = localStorage.getItem(STRING_ID_ROL_USUARIO_WEB);
                objEnviar.idRolUsuarioWeb = idRolUsuarioWeb;
                const response = await obtenerPermisoRolUsuarioWeb(objEnviar);
                if (response.error === false && response.data !== undefined) {
                    const permisos = JSON.parse(response.data.permisos);
                    if (Array.isArray(permisos) && permisos.length > 0) {
                        const manage = permisos.find((p) => p.id === 8);
                        if (manage) {
                            const permisosPage = manage.subMenu.find(
                                (p) =>
                                    p.value ===
                                    PAGE_ID.GESTIONAR_PROGRAMACION_CAMIONES_KANBAN
                            );
                            tieneAcceso = permisosPage?.checked === true;
                        }
                    }
                }
            }
            if (!tieneAcceso) return;

            await cargarViajes();
        } catch (err) {
            console.log("iniciar err");
            console.log(err);
        } finally {
            setCargandoPage(false);
            setTieneAccesoAlModulo(tieneAcceso);
        }
    };

    const cargarViajes = async () => {
        setProcessingVisible(true);
        try {
            const [
                kanbanViaje,
                programacionCamiones,
                kanbanProducto,
                productosAprobados,
            ] = await Promise.all([
                obtenerListaKanban({ idTablero: ID_TABLERO }, false, t),
                obtenerListaProgramacionCamionesAprobados(
                    { idTablero: ID_TABLERO },
                    false,
                    t
                ),
                obtenerListaKanban({ idTablero: ID_TABLERO_PRODUCTO }, false, t),
                obtenerListaSolicitudProductoAprobado(
                    { idTablero: ID_TABLERO_PRODUCTO },
                    false,
                    t
                ),
            ]);
            const [responseStatusViaje, responseStatusProducto] = await Promise.all([
                obtenerStatusKanban({ idTablero: ID_TABLERO }),
                obtenerStatusKanban({ idTablero: ID_TABLERO_PRODUCTO }),
            ]);

            const kanbanViajeLista = Array.isArray(kanbanViaje) ? kanbanViaje : [];
            const programacionLista = (
                Array.isArray(programacionCamiones) ? programacionCamiones : []
            ).map(parseProgramacionCamionesItem);
            const statusViajeLista =
                responseStatusViaje?.error === false ? responseStatusViaje.data : [];

            const kanbanProductoLista = Array.isArray(kanbanProducto)
                ? kanbanProducto
                : [];
            const productosLista = (
                Array.isArray(productosAprobados) ? productosAprobados : []
            ).map(transformProductoAsViaje);
            const statusProductoLista =
                responseStatusProducto?.error === false
                    ? responseStatusProducto.data
                    : [];

            const iniciaViajeColumnViaje = kanbanViajeLista.find(esColumnaIniciaViaje);
            const iniciaViajeColumnProducto = kanbanProductoLista.find(
                esColumnaIniciaViaje
            );

            if (!iniciaViajeColumnViaje && !iniciaViajeColumnProducto) {
                setViajes([]);
                setViajeSelected(null);
                showToast(t("alert_no_hay_columna_inicia_viaje"), "warning", 4000);
                return;
            }

            const itemsViaje = iniciaViajeColumnViaje
                ? collectItemsForColumn(
                    iniciaViajeColumnViaje.id,
                    programacionLista,
                    statusViajeLista
                )
                : [];
            const itemsProductoRaw = iniciaViajeColumnProducto
                ? collectItemsForColumn(
                    iniciaViajeColumnProducto.id,
                    productosLista,
                    statusProductoLista
                )
                : [];
            const itemsProducto = itemsProductoRaw.map((item) =>
                item?.__source === "producto" ? item : transformProductoAsViaje(item)
            );

            const itemsIniciaViaje = [...itemsViaje, ...itemsProducto];
            setViajes(itemsIniciaViaje);
            setViajeSelected((prev) => {
                if (prev == null) return null;
                const stillHere = itemsIniciaViaje.find(
                    (v) =>
                        String(v.id) === String(prev.id) &&
                        (v?.__source ?? "viaje") === (prev?.__source ?? "viaje")
                );
                return stillHere ?? null;
            });
        } catch (err) {
            console.log("cargarViajes err");
            console.log(err);
            showToast(t("alert_error_al_cargar_datos") || "Error", "error");
        } finally {
            setProcessingVisible(false);
        }
    };

    const showToast = (message, type = "warning", durationMs = 3000) => {
        setToastMessage(message);
        setToastType(type);
        setToastVisible(true);
        window.clearTimeout(showToast._timer);
        showToast._timer = window.setTimeout(() => {
            setToastVisible(false);
        }, durationMs);
    };

    const handleViajeClick = (viaje) => {
        if (viaje == null) return;
        setViajeSelected((prev) =>
            prev != null && String(prev.id) === String(viaje.id) ? prev : viaje
        );
        // El reset del panel + hidratación del orden vigente vive en el useEffect
        // que observa `viajeSelected` (más abajo).
    };

    // Lee una respuesta del viaje por key exacta o, como fallback, por bandera booleana en el
    // formulario (ej. `fecha_inicio_viaje: true`). Devuelve el value.
    const getRespuestaValueByKeyOrFlag = (viaje, key, flag) => {
        const arr = viaje?.respuestasParsed;
        if (!Array.isArray(arr)) return null;
        const byKey = arr.find((r) => r?.key === key);
        if (byKey?.value != null && byKey.value !== "") return byKey.value;
        const byFlag = arr.find((r) => r?.[flag] === true);
        return byFlag?.value ?? null;
    };

    // Combina fecha (YYYY-MM-DD) + hora (HH:mm) en un DATETIME MySQL.
    const buildFechaHoraSql = (fecha, hora) => {
        if (!fecha) return null;
        const h = typeof hora === "string" && hora.length >= 4 ? hora : "00:00";
        return `${fecha} ${h.length === 5 ? `${h}:00` : h}`;
    };

    const deriveVentanaViaje = (pedidosViajes) => {
        let ini = null;
        let fin = null;
        for (const v of Array.isArray(pedidosViajes) ? pedidosViajes : []) {
            const fechaIni = getRespuestaValueByKeyOrFlag(v, "fechainicioviaje", "fecha_inicio_viaje");
            const horaIni = getRespuestaValueByKeyOrFlag(v, "ingresehorapartida", "hora_inicio_viaje");
            const fechaFin = getRespuestaValueByKeyOrFlag(v, "fechafinviaje", "fecha_fin_viaje");
            const horaFin = getRespuestaValueByKeyOrFlag(v, "horallegada", "hora_fin_viaje");
            const inicio = buildFechaHoraSql(fechaIni, horaIni);
            const fInVal = buildFechaHoraSql(fechaFin, horaFin);
            if (inicio && (!ini || inicio < ini)) ini = inicio;
            if (fInVal && (!fin || fInVal > fin)) fin = fInVal;
        }
        return { fechaInicioViaje: ini, fechaFinViaje: fin };
    };

    // Valida que para cada pedido el origen (pickup) aparezca antes que el destino (delivery).
    const secuenciaRespetaPickupDelivery = (secuencia) => {
        const origenesVistos = new Set();
        for (const s of Array.isArray(secuencia) ? secuencia : []) {
            const pid = String(s?.pedidoId ?? "");
            if (s?.tipo === "origen") origenesVistos.add(pid);
            else if (s?.tipo === "destino" && !origenesVistos.has(pid)) return false;
        }
        return true;
    };

    // Recomputa distancias/tiempos de una secuencia dada: fase 1 con haversine (instantáneo)
    // y fase 2 con Directions (real). Actualiza `ordenRecomendado` en cada fase.
    const aplicarSecuenciaConRecalculo = async (secuenciaNueva) => {
        const gen = ordenMutRef.current;
        const startPos = ordenRecomendado?.startPos ?? null;

        // Fase 1: haversine
        let acumKm = 0;
        let anterior = startPos;
        const secuenciaFase1 = secuenciaNueva.map((s) => {
            let km = 0;
            if (anterior != null && s?.coord != null) {
                const metros = sharedHaversine(
                    anterior.lat,
                    anterior.lng,
                    s.coord.lat,
                    s.coord.lng
                );
                km = (metros ?? 0) / 1000;
            }
            acumKm += km;
            if (s?.coord != null) anterior = s.coord;
            return { ...s, distanciaKm: km, duracionMin: null };
        });

        setOrdenRecomendado((prev) =>
            prev == null
                ? prev
                : {
                    ...prev,
                    secuencia: secuenciaFase1,
                    totalKm: acumKm,
                    totalMinutos: null,
                    modificadoManual: 1,
                    origen: "manual",
                    directionsPending: true,
                    directionsOk: null,
                    dirty: true,
                }
        );

        // Fase 2: Directions
        try {
            const path = [startPos, ...secuenciaFase1.map((s) => s.coord)].filter(
                (p) => p != null && isFinite(p.lat) && isFinite(p.lng)
            );
            const legs = await fetchDirectionsLegs(path);
            if (gen !== ordenMutRef.current) return; // stale
            if (Array.isArray(legs) && legs.length === secuenciaFase1.length) {
                let sumKm = 0;
                let sumMin = 0;
                const secuenciaFase2 = secuenciaFase1.map((s, i) => {
                    const leg = legs[i];
                    sumKm += leg.distanciaKm;
                    sumMin += leg.duracionMin;
                    return { ...s, distanciaKm: leg.distanciaKm, duracionMin: leg.duracionMin };
                });
                setOrdenRecomendado((prev) =>
                    prev == null
                        ? prev
                        : {
                            ...prev,
                            secuencia: secuenciaFase2,
                            totalKm: sumKm,
                            totalMinutos: sumMin,
                            directionsPending: false,
                            directionsOk: true,
                        }
                );
            } else {
                setOrdenRecomendado((prev) =>
                    prev == null ? prev : { ...prev, directionsPending: false, directionsOk: false }
                );
            }
        } catch (_) {
            if (gen !== ordenMutRef.current) return; // stale
            setOrdenRecomendado((prev) =>
                prev == null ? prev : { ...prev, directionsPending: false, directionsOk: false }
            );
        }
    };

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        try {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", String(index));
        } catch (_) {
            /* ignore */
        }
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        try {
            e.dataTransfer.dropEffect = "move";
        } catch (_) {
            /* ignore */
        }
        if (dragOverIndex !== index) setDragOverIndex(index);
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDrop = async (e, dropIndex) => {
        e.preventDefault();
        const source = draggedIndex;
        setDraggedIndex(null);
        setDragOverIndex(null);
        if (source == null || source === dropIndex) return;

        const secuencia = Array.isArray(ordenRecomendado?.secuencia)
            ? [...ordenRecomendado.secuencia]
            : [];
        if (
            source < 0 ||
            source >= secuencia.length ||
            dropIndex < 0 ||
            dropIndex >= secuencia.length
        ) {
            return;
        }

        const [moved] = secuencia.splice(source, 1);
        secuencia.splice(dropIndex, 0, moved);

        if (!secuenciaRespetaPickupDelivery(secuencia)) {
            showToast(
                t("alert_orden_pickup_antes_destino") ||
                "No se puede entregar antes de recoger el mismo pedido",
                "error",
                3500
            );
            return;
        }

        await aplicarSecuenciaConRecalculo(secuencia);
    };

    // Transiciones válidas por estado. Terminal (completado/cancelado) no ofrece nuevas
    // transiciones aquí — el backend acepta `forzar: true` pero preferimos no exponerlo por defecto.
    const transicionesPorEstado = {
        borrador: [{ estado: "confirmado", label: "label_confirmar_orden", color: "bg-blue-600 hover:bg-blue-700" }],
        confirmado: [{ estado: "en_curso", label: "label_iniciar_viaje_orden", color: "bg-amber-600 hover:bg-amber-700" }],
        en_curso: [{ estado: "completado", label: "label_completar_orden", color: "bg-emerald-600 hover:bg-emerald-700" }],
        completado: [],
        cancelado: [],
    };

    const estadoBadgeClass = (estado) => {
        switch (estado) {
            case "borrador":
                return "bg-gray-100 text-gray-700 border-gray-200";
            case "confirmado":
                return "bg-blue-100 text-blue-700 border-blue-200";
            case "en_curso":
                return "bg-amber-100 text-amber-700 border-amber-200";
            case "completado":
                return "bg-emerald-100 text-emerald-700 border-emerald-200";
            case "cancelado":
                return "bg-rose-100 text-rose-700 border-rose-200";
            default:
                return "bg-gray-100 text-gray-700 border-gray-200";
        }
    };

    const estadoLabelKey = (estado) => {
        switch (estado) {
            case "borrador":
                return "label_estado_borrador";
            case "confirmado":
                return "label_estado_confirmado";
            case "en_curso":
                return "label_estado_en_curso";
            case "completado":
                return "label_estado_completado";
            case "cancelado":
                return "label_estado_cancelado";
            default:
                return "label_estado_borrador";
        }
    };

    const handleCambiarEstado = async (nuevoEstado) => {
        if (ordenRecomendado?.id == null) {
            showToast(
                t("alert_guardar_antes_cambiar_estado") ||
                "Guarda el orden antes de cambiar el estado",
                "warning",
                3500
            );
            return;
        }
        if (ordenRecomendado.dirty) {
            showToast(
                t("alert_guardar_cambios_pendientes") ||
                "Tienes cambios sin guardar. Guarda primero.",
                "warning",
                3500
            );
            return;
        }
        const idUsuario = localStorage.getItem(STRING_ID_USUARIO_WEB) ?? null;
        setProcessingVisible(true);
        try {
            const resp = await actualizarEstadoViajeOrdenKanban({
                id: ordenRecomendado.id,
                estado: nuevoEstado,
                idUsuarioModificacion: idUsuario,
            });
            if (resp?.error) {
                showToast(
                    resp.message ||
                    t("alert_no_se_pudo_modificar") ||
                    "No se pudo cambiar el estado",
                    "error",
                    3500
                );
                return;
            }
            setOrdenRecomendado((prev) =>
                prev == null ? prev : { ...prev, estado: nuevoEstado }
            );
            showToast(
                t("alert_estado_actualizado") || "Estado actualizado",
                "success",
                2500
            );
        } finally {
            setProcessingVisible(false);
        }
    };

    const handleDeshabilitar = async () => {
        if (ordenRecomendado?.id == null) return;
        const idUsuario = localStorage.getItem(STRING_ID_USUARIO_WEB) ?? null;
        setProcessingVisible(true);
        try {
            const resp = await deshabilitarViajeOrdenKanban({
                id: ordenRecomendado.id,
                idUsuarioModificacion: idUsuario,
            });
            if (resp?.error) {
                showToast(
                    resp.message ||
                    t("alert_no_se_pudo_deshabilitar") ||
                    "No se pudo deshabilitar el orden",
                    "error",
                    3500
                );
                return;
            }
            // El orden ya no es vigente — limpiar estado local y cerrar panel.
            setOrdenRecomendado(null);
            setOrdenPanelVisible(false);
            setDibujarRutaSugerida(false);
            showToast(
                t("alert_orden_deshabilitado") || "Orden eliminado",
                "success",
                2500
            );
        } finally {
            setProcessingVisible(false);
        }
    };

    const handleGuardarOrden = async () => {
        if (ordenRecomendado == null) return;
        const secuencia = Array.isArray(ordenRecomendado.secuencia)
            ? ordenRecomendado.secuencia
            : [];
        if (secuencia.length === 0) {
            showToast(
                t("alert_secuencia_vacia") || "Sin paradas para guardar",
                "warning",
                3000
            );
            return;
        }
        const idEmpresa = localStorage.getItem(STRING_ID_EMPRESA_ULTIMA_MILLA_V3);
        if (!idEmpresa) {
            showToast(
                t("alert_sin_empresa") || "No se pudo detectar la empresa (idEmpresa)",
                "error",
                3500
            );
            return;
        }
        const idUsuario = localStorage.getItem(STRING_ID_USUARIO_WEB) ?? null;

        // Derivar pedidosIds únicos desde la secuencia
        const pedidosIds = Array.from(
            new Set(secuencia.map((s) => s?.pedidoId).filter((id) => id != null))
        ).map((id) => String(id));

        // Ventana de tiempo del viaje (min inicio / max fin) sobre los pedidos incluidos
        const pedidosViajes = viajes.filter((v) =>
            pedidosIds.some((pid) => String(pid) === String(v?.id))
        );
        const ventanaCalc = deriveVentanaViaje(pedidosViajes);
        const fechaInicioViaje =
            ordenRecomendado.fechaInicioViaje ?? ventanaCalc.fechaInicioViaje;
        const fechaFinViaje =
            ordenRecomendado.fechaFinViaje ?? ventanaCalc.fechaFinViaje;

        const payloadBase = {
            placa: ordenRecomendado.placa,
            pedidosCount: pedidosIds.length,
            totalKm: Number(ordenRecomendado.totalKm ?? 0),
            totalMinutos:
                ordenRecomendado.totalMinutos != null
                    ? Number(ordenRecomendado.totalMinutos)
                    : null,
            modificadoManual: Number(ordenRecomendado.modificadoManual ?? 0),
            origen: ordenRecomendado.origen ?? "gps",
            secuencia,
            pedidosIds,
            fechaInicioViaje,
            fechaFinViaje,
            estado: ordenRecomendado.estado ?? "borrador",
            nota: ordenRecomendado.nota ?? null,
        };

        setProcessingVisible(true);
        try {
            if (ordenRecomendado.id) {
                const resp = await actualizarViajeOrdenKanban({
                    ...payloadBase,
                    id: ordenRecomendado.id,
                    idUsuarioModificacion: idUsuario,
                });
                if (resp?.error) {
                    showToast(
                        resp.message ||
                        t("alert_no_se_pudo_modificar") ||
                        "No se pudo actualizar",
                        "error",
                        3500
                    );
                    return;
                }
                setOrdenRecomendado((prev) =>
                    prev == null ? prev : { ...prev, dirty: false }
                );
                showToast(
                    t("alert_orden_actualizado") || "Orden actualizado",
                    "success",
                    2500
                );
            } else {
                const resp = await crearViajeOrdenKanban({
                    ...payloadBase,
                    idEmpresa,
                    idUsuarioCreacion: idUsuario,
                });
                if (resp?.error) {
                    showToast(
                        resp.message ||
                        t("alert_no_se_pudo_crear") ||
                        "No se pudo crear",
                        "error",
                        3500
                    );
                    return;
                }
                setOrdenRecomendado((prev) =>
                    prev == null
                        ? prev
                        : { ...prev, id: resp.id, idEmpresa, dirty: false }
                );
                showToast(
                    t("alert_orden_creado") || "Orden guardado",
                    "success",
                    2500
                );
            }
        } finally {
            setProcessingVisible(false);
        }
    };

    // Entry point del botón: si ya hay un orden con secuencia (guardado o recién calculado),
    // pide confirmación antes de sobreescribir. Si no, calcula directo.
    const handleCalcularOrden = () => {
        const tieneSecuencia =
            ordenRecomendado?.secuencia &&
            Array.isArray(ordenRecomendado.secuencia) &&
            ordenRecomendado.secuencia.length > 0;
        if (tieneSecuencia) {
            setModalRecalcularVisible(true);
            return;
        }
        ejecutarCalcularOrden();
    };

    const ejecutarCalcularOrden = async () => {
        if (viajeSelected == null) return;
        const gen = ordenMutRef.current;
        const placa = getPlacaFromViaje(viajeSelected);
        if (!placa) {
            showToast(t("alert_vehiculo_no_seleccionado") || "Sin vehículo asignado", "warning", 3000);
            return;
        }

        // Pedidos con la misma placa (dentro del listado actual, sin filtrar por período)
        const pedidosMismaPlaca = viajes.filter(
            (v) => v?.id != null && String(getPlacaFromViaje(v)) === String(placa)
        );

        const pedidos = pedidosMismaPlaca
            .map((v) => {
                const origen = getGeocercaCentroid(v, "origen");
                const destino = getGeocercaCentroid(v, "destino");
                if (!origen || !destino) return null;
                return {
                    id: v.id,
                    formularioViaje: v.formularioViaje,
                    origen,
                    destino,
                    origenLabel: getGeocercaLabel(v, "origen"),
                    destinoLabel: getGeocercaLabel(v, "destino"),
                };
            })
            .filter(Boolean);

        if (pedidos.length === 0) {
            showToast(t("alert_no_hay_pedidos_para_ordenar") || "Sin pedidos con origen/destino válidos", "warning", 3000);
            return;
        }

        // Punto de partida: puntoActual del vehículo si está, si no, el origen del primer pedido
        let startPos = null;
        if (puntoActual != null) {
            const lat = parseFloat(puntoActual.LATITUD ?? puntoActual.lat ?? puntoActual.latitud);
            const lng = parseFloat(puntoActual.LONGITUD ?? puntoActual.lng ?? puntoActual.longitud);
            if (isFinite(lat) && isFinite(lng)) startPos = { lat, lng };
        }
        if (!startPos) startPos = pedidos[0].origen;

        // 1) Orden greedy con haversine (rápido, siempre disponible).
        const { secuencia, totalKm } = computarOrdenRecomendado(startPos, pedidos);

        // Mostrar de una el resultado con distancias haversine mientras Directions responde.
        setOrdenRecomendado((prev) => ({
            // Preservamos id/estado si el orden ya venía del backend — el recálculo es un
            // cambio local; la persistencia decide con `dirty` en el paso de Guardar.
            id: prev?.id,
            estado: prev?.estado ?? "borrador",
            nota: prev?.nota ?? null,
            idEmpresa: prev?.idEmpresa,
            placa,
            secuencia,
            totalKm,
            totalMinutos: null,
            pedidosCount: pedidos.length,
            pedidosIds: pedidos.map((p) => p.id),
            fechaInicioViaje: prev?.fechaInicioViaje ?? null,
            fechaFinViaje: prev?.fechaFinViaje ?? null,
            modificadoManual: 0,
            origen: puntoActual != null ? "gps" : "primer_origen",
            startPos,
            desdeGps: puntoActual != null,
            directionsPending: true,
            directionsOk: null,
            dirty: true,
        }));
        setOrdenPanelVisible(true);

        // 2) Pedimos a Google Directions las distancias/duraciones REALES por leg y las sobreescribimos.
        try {
            const path = [startPos, ...secuencia.map((s) => s.coord)];
            const legs = await fetchDirectionsLegs(path);
            if (gen !== ordenMutRef.current) return; // stale (cambió el viaje mientras se calculaba)
            if (Array.isArray(legs) && legs.length === secuencia.length) {
                let sumKm = 0;
                let sumMin = 0;
                const secuenciaReal = secuencia.map((s, i) => {
                    const leg = legs[i];
                    sumKm += leg.distanciaKm;
                    sumMin += leg.duracionMin;
                    return {
                        ...s,
                        distanciaKm: leg.distanciaKm,
                        duracionMin: leg.duracionMin,
                    };
                });
                setOrdenRecomendado((prev) =>
                    prev == null
                        ? prev
                        : {
                            ...prev,
                            secuencia: secuenciaReal,
                            totalKm: sumKm,
                            totalMinutos: sumMin,
                            directionsPending: false,
                            directionsOk: true,
                        }
                );
            } else {
                // Directions falló: marca el flag pero deja el resultado haversine.
                setOrdenRecomendado((prev) =>
                    prev == null
                        ? prev
                        : { ...prev, directionsPending: false, directionsOk: false }
                );
            }
        } catch (_) {
            if (gen !== ordenMutRef.current) return; // stale
            setOrdenRecomendado((prev) =>
                prev == null
                    ? prev
                    : { ...prev, directionsPending: false, directionsOk: false }
            );
        }
    };

    const toggleSeleccion = (viaje) => {
        if (viaje?.id == null) return;
        const id = String(viaje.id);
        setIdsSeleccionados((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const seleccionarTodos = () => {
        setIdsSeleccionados(
            new Set(
                viajesPorFuente
                    .filter((v) => v?.id != null)
                    .map((v) => String(v.id))
            )
        );
    };

    const limpiarSeleccion = () => {
        setIdsSeleccionados(new Set());
    };

    const renderSidebarCard = (viaje) => {
        const isSelected =
            viajeSelected != null && String(viajeSelected.id) === String(viaje.id);
        const isChecked = idsSeleccionados.has(String(viaje?.id));
        const placa = getPlacaFromViaje(viaje);
        const origen = getRespuestaByKeyOrFlag(viaje, "geocercaorigen", "geocerca_origen")?.value?.label;
        const destino = getRespuestaByKeyOrFlag(viaje, "geocercadestino", "geocerca_destino")?.value?.label;
        const fechaIni = getRespuestaByKeyOrFlag(viaje, "fechainicioviaje", "fecha_inicio_viaje")?.value;
        const horaIni = getRespuestaByKeyOrFlag(viaje, "ingresehorapartida", "hora_inicio_viaje")?.value;

        return (
            <div
                key={viaje.id}
                className={`w-full bg-white border rounded-lg p-3 shadow-sm transition-all hover:shadow-md ${isSelected
                    ? "border-blue-500 ring-2 ring-blue-200"
                    : "border-gray-200 hover:border-gray-300"
                    }`}
            >
                <div className="flex items-start gap-2">
                    <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        checked={isChecked}
                        onChange={(e) => {
                            e.stopPropagation();
                            toggleSeleccion(viaje);
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                    <button
                        type="button"
                        onClick={() => handleViajeClick(viaje)}
                        className="flex-1 text-left min-w-0 focus:outline-none"
                    >
                        <div className="flex items-start gap-2 min-w-0">
                            <FileText className="h-4 w-4 shrink-0 mt-0.5 text-gray-500" />
                            <p className="text-sm font-semibold text-gray-900 leading-snug break-words">
                                {viaje?.formularioViaje ?? "—"}
                            </p>
                        </div>
                        {placa ? (
                            <div className="flex items-center gap-2 mt-1.5 min-w-0">
                                <Truck className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                                <p className="text-xs text-gray-600 truncate">{placa}</p>
                            </div>
                        ) : null}
                        {(fechaIni || horaIni) && (
                            <div className="flex items-center gap-2 mt-1.5 min-w-0">
                                <Calendar className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                                <p className="text-xs text-gray-500 truncate">
                                    {[fechaIni, horaIni].filter(Boolean).join(" ")}
                                </p>
                            </div>
                        )}
                        {(origen || destino) && (
                            <p className="text-xs text-gray-600 mt-1.5 break-words">
                                {origen ?? "—"} <span className="text-gray-400">→</span>{" "}
                                {destino ?? "—"}
                            </p>
                        )}
                    </button>
                </div>
            </div>
        );
    };

    const render = () => {
        try {
            if (cargandoPage) return cargandoCard();
            if (!tieneAccesoAlModulo) return sinAccesoCard();

            return (
                <div className="h-screen flex flex-col overflow-hidden">
                    <div className="flex-1 flex overflow-hidden relative">
                        <div
                            className={`${sidebarVisible
                                ? "w-1/4 min-w-[280px] max-w-[340px]"
                                : "w-0"
                                } flex-shrink-0 overflow-hidden relative border-r border-gray-200 bg-gray-50`}
                        >
                            {sidebarVisible && (
                                <div className="h-full flex flex-col">
                                    <div className="px-3 py-3 border-b border-gray-200 bg-white space-y-2">
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="min-w-0">
                                                <h2 className="text-sm font-bold text-gray-900 truncate">
                                                    {t("label_iniciar_viaje")}
                                                </h2>
                                                <p className="text-xs text-gray-500">
                                                    {viajesVisibles.length}
                                                    {" / "}
                                                    {viajes.length} {t("label_viajes")}
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                className="p-1.5 rounded text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                                                onClick={() => cargarViajes()}
                                                title={t("label_buscar")}
                                                aria-label={t("label_buscar")}
                                            >
                                                <RefreshCcw className="h-4 w-4" />
                                            </button>
                                        </div>
                                        <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-xs">
                                            <button
                                                type="button"
                                                onClick={() => setFuenteFilter("todos")}
                                                className={`px-3 py-1.5 ${fuenteFilter === "todos"
                                                    ? "bg-blue-600 text-white"
                                                    : "bg-white text-gray-700 hover:bg-gray-50"
                                                    }`}
                                            >
                                                {t("label_todos")}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setFuenteFilter("viaje")}
                                                className={`px-3 py-1.5 border-l border-gray-200 ${fuenteFilter === "viaje"
                                                    ? "bg-blue-600 text-white"
                                                    : "bg-white text-gray-700 hover:bg-gray-50"
                                                    }`}
                                            >
                                                {t("label_camiones")}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setFuenteFilter("producto")}
                                                className={`px-3 py-1.5 border-l border-gray-200 ${fuenteFilter === "producto"
                                                    ? "bg-blue-600 text-white"
                                                    : "bg-white text-gray-700 hover:bg-gray-50"
                                                    }`}
                                            >
                                                {t("label_productos")}
                                            </button>
                                        </div>
                                        <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-xs">
                                            <button
                                                type="button"
                                                onClick={() => setModoVisualizacion("todos")}
                                                className={`px-3 py-1.5 ${modoVisualizacion === "todos"
                                                    ? "bg-blue-600 text-white"
                                                    : "bg-white text-gray-700 hover:bg-gray-50"
                                                    }`}
                                            >
                                                {t("label_mostrar_todos")}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setModoVisualizacion("seleccionados")}
                                                className={`px-3 py-1.5 border-l border-gray-200 ${modoVisualizacion === "seleccionados"
                                                    ? "bg-blue-600 text-white"
                                                    : "bg-white text-gray-700 hover:bg-gray-50"
                                                    }`}
                                            >
                                                {t("label_mostrar_seleccionados")}
                                                {idsSeleccionados.size > 0
                                                    ? ` (${idsSeleccionados.size})`
                                                    : ""}
                                            </button>
                                        </div>
                                        {modoVisualizacion === "seleccionados" && (
                                            <div className="flex items-center gap-2 text-xs">
                                                <button
                                                    type="button"
                                                    onClick={seleccionarTodos}
                                                    className="text-blue-600 hover:underline"
                                                >
                                                    {t("label_seleccionar_todos")}
                                                </button>
                                                <span className="text-gray-300">|</span>
                                                <button
                                                    type="button"
                                                    onClick={limpiarSeleccion}
                                                    className="text-gray-600 hover:underline"
                                                >
                                                    {t("label_limpiar")}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    {viajeSelected != null && getPlacaFromViaje(viajeSelected) && (
                                        <div className="border-b border-gray-200 bg-white px-3 py-2 space-y-2">
                                            <button
                                                type="button"
                                                onClick={handleCalcularOrden}
                                                className="w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700"
                                            >
                                                <Route className="h-4 w-4" />
                                                {t("label_calcular_orden_recomendado")}
                                            </button>
                                            {ordenPanelVisible && ordenRecomendado != null && (
                                                <div className="rounded-md border border-gray-200 bg-gray-50 p-2 space-y-2">
                                                    {/* Fila 1: título + botón cerrar */}
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div className="min-w-0 flex-1">
                                                            <p className="text-xs font-semibold text-gray-900 break-words">
                                                                {t("label_orden_recomendado")}
                                                            </p>
                                                            <p className="text-[11px] text-gray-500 break-words leading-snug mt-0.5">
                                                                {ordenRecomendado.pedidosCount} {t("label_viajes")} · {(ordenRecomendado.totalKm ?? 0).toFixed(2)} km
                                                                {ordenRecomendado.totalMinutos != null && (
                                                                    <> · {formatDuracion(ordenRecomendado.totalMinutos)}</>
                                                                )}
                                                                {ordenRecomendado.dirty && (
                                                                    <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded align-middle">
                                                                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                                                        {t("label_sin_guardar") || "sin guardar"}
                                                                    </span>
                                                                )}
                                                            </p>
                                                            {ordenRecomendado.directionsPending && (
                                                                <p className="text-[11px] text-blue-600 mt-0.5 break-words leading-snug">
                                                                    {t("label_calculando_tiempos") || "Calculando tiempos reales…"}
                                                                </p>
                                                            )}
                                                            {ordenRecomendado.directionsOk === false && (
                                                                <p className="text-[11px] text-amber-600 mt-0.5 break-words leading-snug">
                                                                    {t("label_directions_fallback") || "Distancias estimadas (línea recta)"}
                                                                </p>
                                                            )}
                                                            {!ordenRecomendado.desdeGps && (
                                                                <p className="text-[11px] text-amber-600 mt-0.5 break-words leading-snug">
                                                                    {t("label_sin_posicion_actual")}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setOrdenPanelVisible(false);
                                                                setDibujarRutaSugerida(false);
                                                            }}
                                                            title={t("label_cerrar") || "Cerrar"}
                                                            aria-label={t("label_cerrar") || "Cerrar"}
                                                            className="p-1 rounded text-gray-500 hover:text-gray-900 hover:bg-gray-200 shrink-0"
                                                        >
                                                            <span className="text-lg leading-none">×</span>
                                                        </button>
                                                    </div>

                                                    {/* Fila 2: botones de acción (wrap para no explotar la sidebar) */}
                                                    <div className="flex flex-wrap items-center gap-1.5">
                                                        {(() => {
                                                            const isEmpty =
                                                                (ordenRecomendado.secuencia?.length ?? 0) === 0;
                                                            const isSavedClean =
                                                                !!ordenRecomendado.id &&
                                                                ordenRecomendado.dirty === false;
                                                            const isDisabled = isEmpty || isSavedClean;
                                                            const label = ordenRecomendado.id
                                                                ? t("label_guardar_cambios") || "Guardar cambios"
                                                                : t("label_guardar") || "Guardar";
                                                            return (
                                                                <button
                                                                    type="button"
                                                                    onClick={handleGuardarOrden}
                                                                    disabled={isDisabled}
                                                                    title={
                                                                        isEmpty
                                                                            ? t("alert_secuencia_vacia") || "Sin paradas"
                                                                            : label
                                                                    }
                                                                    className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded ${isDisabled
                                                                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                                        : "bg-emerald-600 text-white hover:bg-emerald-700"
                                                                        }`}
                                                                >
                                                                    {label}
                                                                </button>
                                                            );
                                                        })()}
                                                        <button
                                                            type="button"
                                                            onClick={() => setDibujarRutaSugerida((v) => !v)}
                                                            className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded ${dibujarRutaSugerida
                                                                ? "bg-blue-600 text-white hover:bg-blue-700"
                                                                : "bg-white border border-blue-600 text-blue-600 hover:bg-blue-50"
                                                                }`}
                                                        >
                                                            <Route className="h-3 w-3" />
                                                            {dibujarRutaSugerida
                                                                ? t("label_ocultar_ruta")
                                                                : t("label_dibujar_ruta")}
                                                        </button>
                                                        {ordenRecomendado.id != null && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setModalDeshabilitarVisible(true)}
                                                                title={t("label_eliminar_orden") || "Eliminar orden"}
                                                                aria-label={t("label_eliminar_orden") || "Eliminar orden"}
                                                                className="ml-auto p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                                                            >
                                                                <Trash2 className="h-3.5 w-3.5" />
                                                            </button>
                                                        )}
                                                    </div>
                                                    {ordenRecomendado.estado && (
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <span
                                                                className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded border ${estadoBadgeClass(
                                                                    ordenRecomendado.estado
                                                                )}`}
                                                            >
                                                                {t(estadoLabelKey(ordenRecomendado.estado))}
                                                            </span>
                                                            {(transicionesPorEstado[ordenRecomendado.estado] ?? []).map(
                                                                (tr) => (
                                                                    <button
                                                                        key={tr.estado}
                                                                        type="button"
                                                                        onClick={() => handleCambiarEstado(tr.estado)}
                                                                        disabled={
                                                                            ordenRecomendado.id == null ||
                                                                            ordenRecomendado.dirty === true
                                                                        }
                                                                        className={`text-[10px] font-medium px-2 py-0.5 rounded text-white ${ordenRecomendado.id == null || ordenRecomendado.dirty === true
                                                                            ? "bg-gray-300 cursor-not-allowed"
                                                                            : tr.color
                                                                            }`}
                                                                        title={
                                                                            ordenRecomendado.id == null
                                                                                ? t("alert_guardar_antes_cambiar_estado") ||
                                                                                "Guarda el orden antes"
                                                                                : ordenRecomendado.dirty
                                                                                    ? t("alert_guardar_cambios_pendientes") ||
                                                                                    "Guarda los cambios primero"
                                                                                    : t(tr.label)
                                                                        }
                                                                    >
                                                                        {t(tr.label)}
                                                                    </button>
                                                                )
                                                            )}
                                                            {["borrador", "confirmado", "en_curso"].includes(
                                                                ordenRecomendado.estado
                                                            ) && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleCambiarEstado("cancelado")}
                                                                        disabled={
                                                                            ordenRecomendado.id == null ||
                                                                            ordenRecomendado.dirty === true
                                                                        }
                                                                        className={`text-[10px] font-medium px-2 py-0.5 rounded ${ordenRecomendado.id == null || ordenRecomendado.dirty === true
                                                                            ? "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
                                                                            : "bg-white text-rose-600 border border-rose-300 hover:bg-rose-50"
                                                                            }`}
                                                                    >
                                                                        {t("label_cancelar_orden") || "Cancelar"}
                                                                    </button>
                                                                )}
                                                        </div>
                                                    )}
                                                    {ordenRecomendado.secuencia.length === 0 ? (
                                                        <p className="text-[11px] text-gray-500">
                                                            {t("label_sin_datos")}
                                                        </p>
                                                    ) : (
                                                        <ol
                                                            className="space-y-1"
                                                            onDragLeave={handleDragLeave}
                                                        >
                                                            {ordenRecomendado.secuencia.map((s, i) => {
                                                                const isDragging = draggedIndex === i;
                                                                const isDragOver =
                                                                    dragOverIndex === i && draggedIndex !== i;
                                                                return (
                                                                    <li
                                                                        key={`${s.pedidoId}-${s.tipo}-${i}`}
                                                                        draggable
                                                                        onDragStart={(e) => handleDragStart(e, i)}
                                                                        onDragOver={(e) => handleDragOver(e, i)}
                                                                        onDrop={(e) => handleDrop(e, i)}
                                                                        onDragEnd={handleDragEnd}
                                                                        className={`flex items-start gap-2 text-[11px] text-gray-800 px-1 py-1 rounded transition-colors ${isDragging
                                                                            ? "opacity-40"
                                                                            : isDragOver
                                                                                ? "bg-blue-50 border-t-2 border-blue-400"
                                                                                : "hover:bg-gray-100"
                                                                            }`}
                                                                        title={t("label_arrastrar_para_reordenar") || "Arrastrar para reordenar"}
                                                                    >
                                                                        <GripVertical
                                                                            className="h-3.5 w-3.5 shrink-0 text-gray-300 mt-0.5 cursor-grab active:cursor-grabbing"
                                                                        />
                                                                        <span className="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-white border border-gray-300 text-[10px] font-semibold text-gray-700">
                                                                            {i + 1}
                                                                        </span>
                                                                        <div className="min-w-0 flex-1">
                                                                            <p className="truncate">
                                                                                <span className={`font-semibold ${s.tipo === "origen" ? "text-emerald-700" : "text-rose-700"}`}>
                                                                                    {s.tipo === "origen" ? t("label_recoger") : t("label_entregar")}
                                                                                </span>
                                                                                {": "}
                                                                                {s.geocercaLabel ?? "—"}
                                                                            </p>
                                                                            <p className="text-[10px] text-gray-500 truncate">
                                                                                {s.pedidoLabel} · {(s.distanciaKm ?? 0).toFixed(2)} km
                                                                                {s.duracionMin != null && (
                                                                                    <> · {formatDuracion(s.duracionMin)}</>
                                                                                )}
                                                                            </p>
                                                                        </div>
                                                                        <MapPin className="h-3.5 w-3.5 shrink-0 text-gray-400 mt-0.5" />
                                                                    </li>
                                                                );
                                                            })}
                                                        </ol>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className="flex-1 overflow-y-auto p-2 space-y-2">
                                        {viajesPorFuente.length === 0 ? (
                                            <div className="flex items-center justify-center h-24 text-xs text-gray-400 border border-dashed border-gray-200 rounded-lg">
                                                {t("label_sin_datos")}
                                            </div>
                                        ) : (
                                            viajesPorFuente.map((v) => renderSidebarCard(v))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex-1 min-w-0 relative" style={{ zIndex: 1 }}>
                            <MapaViajeGoogle
                                viajes={viajesVisibles}
                                viajeSelected={viajeSelected}
                                puntoActual={puntoActual}
                                onViajeClick={handleViajeClick}
                                vehiculosPuntos={(() => {
                                    const placaSel = viajeSelected
                                        ? getPlacaFromViaje(viajeSelected)
                                        : null;
                                    return Object.entries(puntosVehiculos)
                                        .filter(([placa, punto]) => {
                                            if (punto == null) return false;
                                            if (placaSel && String(placa) === String(placaSel)) return false;
                                            return true;
                                        })
                                        .map(([placa, punto]) => ({ placa, punto }));
                                })()}
                                secuenciaSugerida={
                                    dibujarRutaSugerida && ordenRecomendado
                                        ? {
                                            secuencia: ordenRecomendado.secuencia,
                                            startPos: ordenRecomendado.startPos,
                                        }
                                        : null
                                }
                            />
                        </div>

                        {!sidebarVisible && (
                            <button
                                onClick={() => setSidebarVisible(true)}
                                className="absolute bg-white border border-gray-300 rounded-r-lg shadow-lg hover:shadow-xl transition-all duration-200 hover:bg-gray-50 flex items-center justify-center"
                                style={{
                                    height: "2.5rem",
                                    width: "2.5rem",
                                    top: "4.25rem",
                                    transform: "translateY(-50%)",
                                    left: 0,
                                    zIndex: 99999,
                                }}
                                aria-label={t("label_mostrar_sidebar") || "Mostrar"}
                            >
                                <ChevronRight className="h-5 w-5 text-gray-700" />
                            </button>
                        )}
                    </div>

                    <ProcessingDialog
                        visible={processingVisible}
                        message={t("alert_procesando_solicitud")}
                    />
                    <ToastComponent
                        visible={toastVisible}
                        message={toastMessage}
                        type={toastType}
                        onClose={() => setToastVisible(false)}
                    />
                    <ModalConfirmar
                        isOpen={modalRecalcularVisible}
                        onCancel={() => setModalRecalcularVisible(false)}
                        onConfirm={() => {
                            setModalRecalcularVisible(false);
                            ejecutarCalcularOrden();
                        }}
                        message={
                            ordenRecomendado?.id
                                ? t("alert_confirmar_recalcular_guardado") ||
                                "Ya hay un orden guardado para este vehículo. ¿Recalcular y reemplazar la secuencia? Los cambios no se guardarán hasta que hagas click en Guardar."
                                : t("alert_confirmar_recalcular") ||
                                "Ya calculaste un orden. ¿Recalcular y reemplazar la secuencia actual?"
                        }
                    />
                    <ModalConfirmar
                        isOpen={modalDeshabilitarVisible}
                        onCancel={() => setModalDeshabilitarVisible(false)}
                        onConfirm={() => {
                            setModalDeshabilitarVisible(false);
                            handleDeshabilitar();
                        }}
                        message={
                            t("alert_confirmar_deshabilitar_orden") ||
                            "¿Eliminar (deshabilitar) este orden? Podrás rehabilitarlo desde el historial."
                        }
                    />
                </div>
            );
        } catch (err) {
            console.log("MapaViajePage render err");
            console.log(err);
            return (
                <div>
                    <label>{JSON.stringify(err)}</label>
                </div>
            );
        }
    };

    return render();
}
